/**
 * The surface the command palette sits in: finixt-ui's CommandPalette panel
 * on Radix Dialog, which supplies the trap, the restore, Escape, and the
 * scrim click. The list inside is the caller's, so cmdk stays out of this
 * layer and Radix stays in it.
 */
import * as Dialog from '@radix-ui/react-dialog';
import { Command } from 'cmdk';
import type { ComponentProps, CSSProperties, ReactNode } from 'react';

import { useReturnFocus } from './useReturnFocus.js';

const SCRIM_Z_INDEX = 90;

const HIDDEN_TITLE: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export interface CommandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Accessible name of the dialog. */
  label: string;
  children: ReactNode;
}

/** The scrim and the panel. */
export function CommandDialog({ open, onOpenChange, label, children }: CommandDialogProps) {
  // Radix returns focus to its own Trigger only; the palette opens from the
  // rail's search row or from ⌘K, so it remembers the opener itself.
  const onCloseAutoFocus = useReturnFocus(open);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: SCRIM_Z_INDEX,
            background: 'var(--scrim)',
            animation: 'fadein var(--dur-fast) var(--ease-out)',
          }}
        />
        <Dialog.Content
          aria-describedby={undefined}
          onCloseAutoFocus={onCloseAutoFocus}
          style={{
            position: 'fixed',
            zIndex: SCRIM_Z_INDEX + 1,
            top: 96,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 560,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(64vh, 520px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-sheet)',
            boxShadow: 'var(--shadow-sheet)',
            outline: 'none',
            animation: 'popin var(--dur-enter) var(--ease-out)',
          }}
        >
          <Dialog.Title style={HIDDEN_TITLE}>{label}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** One row of the palette; cmdk's selection highlights it through layer.css. */
export function PaletteRow(props: ComponentProps<typeof Command.Item>) {
  return <Command.Item {...props} className="ui-palette-item" />;
}
